/*!
 * Copyright (c) Boa-Franc.
 * P2534-129 : lignes de l'état de compte servies à la page Factures.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';

import { boaGetInvoiceLinesAsync } from './DataService/DataActionExtension.g';
import { IBoaGetInvoiceLinesResponse } from './DataService/DataServiceEntities.g';

/**
 * Statuts de règlement, alignés sur InvoicePaidStatus du proxy standard. Repris ici pour
 * que l'appelant n'ait pas à importer le proxy juste pour nommer un entier.
 */
export const BOA_PAID_STATUS_ALL = 0;
export const BOA_PAID_STATUS_UNPAID = 1;
export const BOA_PAID_STATUS_PARTIALLY_PAID = 2;
export const BOA_PAID_STATUS_PAID = 3;

/**
 * Entrée de l'action de lecture des lignes de facture.
 */
export class BoaGetInvoiceLinesInput implements IActionInput {
    public customerAccountNumber: string;

    public businessPartnerId: string;

    public paidStatus: number;

    public skip: number;

    public top: number;

    public invoiceIds: string[];

    public constructor(
        customerAccountNumber: string,
        businessPartnerId: string,
        paidStatus: number,
        skip: number,
        top: number,
        invoiceIds: string[] = []
    ) {
        this.customerAccountNumber = customerAccountNumber;
        this.businessPartnerId = businessPartnerId;
        this.paidStatus = paidStatus;
        this.skip = skip;
        this.top = top;
        this.invoiceIds = invoiceIds;
    }

    /**
     * Aucune mise en cache : les montants dus évoluent au fil des paiements, et l'en-tête
     * porte un solde qui doit être juste à l'affichage. La clé reste discriminante par
     * compte, statut et tranche afin qu'un cache activé par erreur ne serve jamais la
     * tranche d'un autre client.
     */
    public getCacheKey = () =>
        `BoaGetInvoiceLinesInput-${this.customerAccountNumber}-${this.paidStatus}-${this.skip}-${this.top}-${this.invoiceIds.join('|')}`;

    public getCacheObjectType = () => 'BoaGetInvoiceLinesInput';

    public dataCacheType = (): CacheType => 'none';
}

const createInput = (
    args: ICreateActionContext,
    customerAccountNumber: string,
    businessPartnerId: string,
    paidStatus: number,
    skip: number,
    top: number,
    invoiceIds: string[]
): IActionInput => {
    return new BoaGetInvoiceLinesInput(customerAccountNumber, businessPartnerId, paidStatus, skip, top, invoiceIds);
};

/**
 * Interroge le CSU. Une erreur est journalisée puis relancée : le module affiche son
 * propre message et ne réessaie jamais automatiquement.
 */
export async function BoaGetInvoiceLines(input: BoaGetInvoiceLinesInput, ctx: IActionContext): Promise<IBoaGetInvoiceLinesResponse> {
    return boaGetInvoiceLinesAsync(
        { callerContext: ctx },
        input.customerAccountNumber,
        input.businessPartnerId,
        input.paidStatus,
        input.skip,
        input.top,
        input.invoiceIds
    )
        .then(data => {
            return data;
        })
        .catch(error => {
            ctx.telemetry.exception(error);
            ctx.trace('[boa-get-invoice-lines] Unable to read the invoice lines');
            throw new Error('[boa-get-invoice-lines] Unable to read the invoice lines');
        });
}

export default createObservableDataAction({
    action: <IAction<IBoaGetInvoiceLinesResponse>>BoaGetInvoiceLines,
    id: 'BoaGetInvoiceLines',
    input: createInput
});
